import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash, FloppyDisk, Plus, PencilSimple } from "@phosphor-icons/react";

/**
 * Generic CMS content editor. Reads a JSON-schema-lite via `fields` array
 * and lets admin add/edit/delete items for a given content type.
 */
export default function ContentManager({ resource, label, fields, listColumns }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, {} new, {..slug} edit
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    return api.get(`/${resource}/all`).then(r => setItems(r.data)).catch(() => {});
  }, [resource]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...editing };
      // Coerce comma-separated list fields to arrays
      fields.forEach(f => {
        if (f.type === "list" && typeof body[f.name] === "string") {
          body[f.name] = body[f.name].split("\n").map(x => x.trim()).filter(Boolean);
        }
      });
      if (editing._new) {
        delete body._new;
        await api.post(`/${resource}`, body);
        toast.success(`${label} created`);
      } else {
        const originalSlug = editing._originalSlug || editing.slug;
        delete body._originalSlug;
        await api.put(`/${resource}/${originalSlug}`, body);
        toast.success(`${label} updated`);
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this item? This cannot be undone.")) return;
    try {
      await api.delete(`/${resource}/${slug}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Could not delete"); }
  };

  if (editing !== null) {
    return (
      <div data-testid={`${resource}-editor`}>
        <div className="eyebrow text-solar mb-2">{label}</div>
        <h1 className="mb-8 text-3xl">{editing._new ? `New ${label}` : `Edit ${label}`}</h1>
        <div className="border border-chrome bg-steel p-6 max-w-3xl space-y-4">
          {fields.map(f => (
            <FieldRow key={f.name} field={f} value={editing[f.name]} onChange={(v) => setEditing({ ...editing, [f.name]: v })} />
          ))}
          <div className="pt-4 border-t border-chrome flex items-center justify-between">
            <button data-testid="editor-cancel" onClick={() => setEditing(null)} className="btn-ghost text-sm">Cancel</button>
            <button data-testid="editor-save" onClick={save} disabled={busy} className="btn-solar text-sm inline-flex items-center gap-2"><FloppyDisk size={14} weight="fill" /> {busy ? "Saving…" : "Save"}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`${resource}-list`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow text-solar mb-2">CMS</div>
          <h1 className="text-3xl">{label}</h1>
        </div>
        <button data-testid={`add-${resource}`} onClick={() => setEditing({ _new: true, status: "draft" })} className="btn-solar inline-flex items-center gap-2"><Plus size={14} weight="bold" /> New {label}</button>
      </div>
      <div className="border border-chrome bg-steel overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead>
            <tr className="border-b border-chrome text-left">
              {listColumns.map(c => <th key={c.name} className="p-3 text-[11px] uppercase tracking-widest text-white/60">{c.label}</th>)}
              <th className="p-3 text-[11px] uppercase tracking-widest text-white/60 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.slug} className="border-b border-chrome hover:bg-obsidian">
                {listColumns.map(c => (
                  <td key={c.name} className="p-3 text-sm text-white/85">
                    {c.name === "status" ? <StatusPill status={it[c.name]} /> : (String(it[c.name] || "—")).substring(0, 80)}
                  </td>
                ))}
                <td className="p-3 text-right">
                  <button data-testid={`edit-${it.slug}`} onClick={() => setEditing({ ...it, _originalSlug: it.slug })} className="text-solar text-xs uppercase tracking-widest font-bold mr-4 inline-flex items-center gap-1"><PencilSimple size={12} weight="bold" /> Edit</button>
                  <button data-testid={`delete-${it.slug}`} onClick={() => remove(it.slug)} className="text-red-400 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"><Trash size={12} weight="bold" /> Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={listColumns.length + 1} className="p-6 text-center text-white/60 text-sm">No {label.toLowerCase()} yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FieldRow({ field, value, onChange }) {
  const v = value ?? (field.type === "list" ? [] : "");
  const label = <div className="eyebrow text-white/70 mb-1.5">{field.label}</div>;
  if (field.type === "textarea") {
    return <label className="block">{label}<textarea data-testid={`field-${field.name}`} value={v} onChange={(e) => onChange(e.target.value)} rows={field.rows || 5} className="w-full bg-obsidian border border-chrome px-3 py-2 text-sm text-white focus:border-solar outline-none" /></label>;
  }
  if (field.type === "list") {
    return <label className="block">{label}
      <textarea data-testid={`field-${field.name}`} placeholder="One item per line" value={Array.isArray(v) ? v.join("\n") : v} onChange={(e) => onChange(e.target.value)} rows={field.rows || 5} className="w-full bg-obsidian border border-chrome px-3 py-2 text-sm text-white focus:border-solar outline-none" />
    </label>;
  }
  if (field.type === "select") {
    return <label className="block">{label}
      <select data-testid={`field-${field.name}`} value={v} onChange={(e) => onChange(e.target.value)} className="w-full bg-obsidian border border-chrome px-3 py-2 text-sm text-white focus:border-solar outline-none">
        {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>;
  }
  return <label className="block">{label}
    <input data-testid={`field-${field.name}`} type={field.type || "text"} value={v} onChange={(e) => onChange(e.target.value)} className="w-full bg-obsidian border border-chrome px-3 py-2 text-sm text-white focus:border-solar outline-none" />
  </label>;
}

function StatusPill({ status }) {
  const s = status || "draft";
  const map = { published: "bg-solar text-black", draft: "bg-white/20 text-white/70", in_review: "bg-blue-500 text-white", approved: "bg-emerald-500 text-black", archived: "bg-red-500/20 text-red-300" };
  return <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${map[s] || "bg-white/10 text-white/70"}`}>{s}</span>;
}
